import { Flex, Table, Tooltip, Tag } from "antd"
import type { TableProps } from "antd"
import { useMemo } from "react"
import { uniqBy } from "lodash-es"

import { getDomainKwargs } from "src/global/utils/getExpectationGroupName.ts"
import { graphql } from "src/api/graphql/gql.ts"
import {
  ExpectationValidationResultV2,
  Domain,
  ExpectationsTable_ExpectationFragment,
  RenderedExpectationFragment,
} from "src/api/graphql/graphql.ts"
import { EditExpectationFragment } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/SimpleEditExpectationDrawer.tsx"
import { renderExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/utils.tsx"
import { Button } from "src/global/ui/Button/Button.tsx"
import {
  DataQualityIssue,
  DataQualityIssues,
} from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"
import { DeleteExpectationModal } from "src/pages/DataAssets/views/Expectations/DeleteExpectationModal.tsx"
import { FragmentType, unmaskFragment } from "src/api/graphql/fragment-masking.ts"
import { Image } from "src/global/ui/Image"
import { Icon } from "src/global/ui/Icon"
import { parseMetaNotes } from "src/pages/DataAssets/views/Expectations/Expectation/utils/parsing.ts"
import { parseJSONStringConfig } from "src/pages/DataAssets/views/AssetDetails/utils.tsx"
import { pluralize } from "src/global/utils/strings.ts"
import { MetaNotes } from "src/pages/DataAssets/views/Expectations/Expectation/MetaNotes.tsx"
import { Sentry } from "src/global/observability/sentry/sentry.ts"
import { theme } from "src/global/ui/themes/theme.ts"
import { useRequireRole } from "src/global/hooks/useRequireRole"
import {
  getExpectationMetaInfo,
  GetExpectationMetaInfo_ExpectationFragmentDocument,
} from "src/global/schemas/expectations/expectation-metadata-utils"
import { formatLocalCalendarDateWithTime } from "src/global/utils/formatTime.ts"
import { BodyNormal } from "src/global/ui/typography/Text/Text"
import { styled } from "styled-components"
import { DqiCoverageIcon } from "src/pages/DataAssets/components/DqiCoverageIcon"
import { useExpectationFilter } from "src/pages/DataAssets/views/Expectations/hooks/useExpectationFilter"

export const anomaly_detection_expectations_created_via = ["asset_creation"]

export const ExpectationsTable_ExpectationFragmentDocument = graphql(`
  fragment ExpectationsTable_Expectation on ExpectationConfiguration {
    geCloudId
    gxManaged
    autogenerated
    createdVia
    kwargs
    expectationType
    description
    domain {
      domainKwargs
      domainType
      id
      domainColumns {
        name
        order
      }
    }
    renderedContent {
      ...RenderedExpectation
    }
    validationResults(input: { limit: 1 }) {
      runTime
      success
    }
    ...GetExpectationMetaInfo_Expectation
  }
`)

type ExpectationTableExpectationFragment = FragmentType<typeof ExpectationsTable_ExpectationFragmentDocument>

interface ExpectationsTableProps {
  antdTableProps: TableProps
  assetId: string
  data: ExpectationTableExpectationFragment[]
  loading: boolean
  id: string
  setEditingExpectationFragment: (fragment: EditExpectationFragment) => void
  dataTestid?: string
}

export type DataType = {
  expectation: ExpectationData
  lastValidated: Omit<ExpectationValidationResultV2, "__typename">
  actions: { expectationFragment: EditExpectationFragment; expectationId: string | null }
  children?: DataType[]
  isGroupRow?: boolean
  isFilterable: boolean
}

const noExpectationsEmptyRowId = "no-expectations-empty-row"

export const noExpectationEmptyRow = (id: string) => ({
  expectation: {
    content: null,
    id: `${noExpectationsEmptyRowId}-${id}`,
    autogenerated: false,
    sortValue: "",
    domainName: "",
    truncateInfo: { truncate: false, fullName: "No Expectations" },
    renderExpectationColumn: () => <BodyNormal $color="colorTextTertiary">No Expectations</BodyNormal>,
  },
  lastValidated: {
    success: null,
    runTime: null,
  },
  actions: {
    expectationId: null,
    expectationFragment: null as unknown as EditExpectationFragment,
  },
  isFilterable: false,
})

export interface ExpectationData extends DomainData {
  content: string | JSX.Element | null
  renderExpectationColumn: (record: DataType) => JSX.Element
  id: string
  autogenerated: boolean
  dataQualityIssue?: string
  createdVia?: string | null
}

type DomainData = {
  domainName: string
  sortValue: string
  truncateInfo: {
    truncate: boolean
    fullName: string
    restColumns?: string
  }
}

const assetDomainName = "Asset"
const customSQLDomainName = "Custom SQL"
const customSQLExpectationType = "unexpected_rows_expectation"
const firstLexicalCharacter = String.fromCharCode(0)

const StyledTable = styled(Table<DataType>)`
  .ant-table-row[data-row-key^="group-"] {
    cursor: pointer;
  }

  /* Ensure consistent alignment of DQI icons in group rows */
  .ant-table-row[data-row-key^="group-"] .ant-flex {
    align-items: center;
  }

  /* Fix alignment of icons and images in group rows */
  .ant-table-row[data-row-key^="group-"] svg,
  .ant-table-row[data-row-key^="group-"] img {
    display: block;
  }
`

export const ExpectationsTable = (props: ExpectationsTableProps) => {
  const { assetId, data, id, loading, setEditingExpectationFragment, antdTableProps, dataTestid } = props

  const unmaskedData = unmaskFragment(ExpectationsTable_ExpectationFragmentDocument, data) || []

  const { filteredExpectations: filteredUnmaskedData } = useExpectationFilter(unmaskedData)

  const { dataSource, expandable } = useMemo(() => {
    const resolvedDataSource = getGroupedExpectationData(filteredUnmaskedData)

    const expandableConfig: TableProps<DataType>["expandable"] = {
      indentSize: 4,
      expandIcon: ({ expanded, onExpand, record }) => {
        if (record.isGroupRow) {
          if (record?.children?.length === 0) {
            // TODO: Find a better solution. Mutating the record was the only working approach to get the empty row to render when a filter was applied.
            record.children = [noExpectationEmptyRow(record.expectation.domainName)]
          }
          return (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onExpand(record, e)
              }}
              type="text"
              size="small"
              aria-label={expanded ? "Collapse group" : "Expand group"}
              icon={expanded ? "chevronDown" : "chevronRight"}
            />
          )
        }
        return null
      },
      defaultExpandAllRows: false,
      expandRowByClick: true,
      rowExpandable: (record: DataType) => record.isGroupRow === true,
    }

    return {
      dataSource: resolvedDataSource,
      expandable: expandableConfig,
    }
  }, [filteredUnmaskedData])

  const isUserEditorOrHigher = useRequireRole("EDITOR")

  return (
    <StyledTable
      {...antdTableProps}
      id={id}
      data-testid={dataTestid}
      columns={getColumns(unmaskedData, assetId ?? "", isUserEditorOrHigher, setEditingExpectationFragment)}
      dataSource={dataSource}
      loading={loading}
      pagination={false}
      rowKey={({ expectation }: DataType) => expectation.id}
      expandable={expandable}
    />
  )
}

const getColumns = (
  data: ExpectationsTable_ExpectationFragment[],
  assetId: string,
  isUserEditorOrHigher: boolean,
  onEdit: (expectationFragment: EditExpectationFragment) => void,
): TableProps<DataType>["columns"] => {
  const columns: TableProps<DataType>["columns"] = [
    {
      dataIndex: "aiOrExplicit",
      key: "aiOrExplicit",
      width: "40px",
      onCell: (record) => ({
        style: record.isGroupRow ? {} : { verticalAlign: "top" },
      }),
      render: (_, { isGroupRow, expectation }) => {
        if (
          expectation.autogenerated &&
          anomaly_detection_expectations_created_via.includes(expectation.createdVia ?? "")
        ) {
          return (
            <Flex justify="start" style={{ paddingTop: "7px" }}>
              <Flex gap="small" align="center" justify="center">
                <Image
                  type="sparkles"
                  aria-label="Anomaly Detection"
                  svgProps={{ height: "16px", width: "16px", color: "#8c8c8c" }}
                />
              </Flex>
            </Flex>
          )
        }
        if (!isGroupRow && !expectation.id.includes(noExpectationsEmptyRowId)) {
          return (
            <Flex justify="start" style={{ paddingTop: "4px" }}>
              <Flex gap="small" align="center" justify="center">
                <Icon name="sliders" color="#8c8c8c" size="16px" aria-label="Explicit" />
              </Flex>
            </Flex>
          )
        }
      },
    },
    {
      title: "Expectation",
      dataIndex: "expectation",
      key: "exp",
      filters: getDomainFilter(data),
      onFilter: (value, record) => {
        if (!record.isFilterable) {
          return true
        }
        return record.expectation.domainName === value
      },
      filterMultiple: true,
      render: (_, record) => record.expectation.renderExpectationColumn(record),
    },
    {
      title: "Last validated",
      dataIndex: "lastValidated",
      key: "lastValidated",
      filters: [
        { text: "Pass", value: true },
        { text: "Fail", value: false },
      ],
      onFilter: (value, record) => {
        if (!record.isFilterable) {
          return true
        }
        return record.lastValidated.success === value
      },
      filterMultiple: false,
      width: "250px",
      onCell: (record) => ({
        style: record.isGroupRow ? {} : { verticalAlign: "top" },
      }),
      render: (_, { lastValidated, isGroupRow, children }) => {
        if (isGroupRow && children && children.length > 0) {
          const validatedCount = children.filter((child) => child.lastValidated.runTime !== null).length
          if (validatedCount === 0) {
            return null
          }
          const failedCount = children.filter(
            (child) => child.lastValidated.success === false && child.lastValidated.runTime !== null,
          ).length
          const totalCount = children.length
          if (failedCount === 0) {
            return (
              <Flex justify="start">
                <Tag color="green">All passed</Tag>
              </Flex>
            )
          }
          if (failedCount > 0) {
            return (
              <Flex justify="start">
                <Tag color="error">
                  {failedCount}/{totalCount} Failed
                </Tag>
              </Flex>
            )
          }
        }
        const { success, runTime } = lastValidated
        if (runTime === null && success === null) {
          return null
        }
        const icon = success ? "success" : "failure"
        return (
          <Flex justify="start" style={{ paddingTop: 4 }}>
            <Flex gap="small" align="center" justify="center">
              <Image style={{ height: 24 }} aria-label={icon} type={icon} /> <span>{runTime}</span>
            </Flex>
          </Flex>
        )
      },
    },
  ]
  if (isUserEditorOrHigher) {
    columns.push({
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "96px",
      onCell: (record) => ({
        style: record.isGroupRow ? {} : { verticalAlign: "top" },
      }),
      render: (_, { actions, isGroupRow, children }) => {
        if (isGroupRow && children) {
          const expectationIds = children.map((child) => child.actions.expectationId).filter(Boolean) as string[]
          if (expectationIds.length > 0) {
            return (
              <Flex justify="end">
                <DeleteExpectationModal
                  expectationIds={expectationIds}
                  assetId={assetId}
                  isBulkDelete={true}
                  stopPropagation={true}
                />
              </Flex>
            )
          }
          return null
        }

        const { expectationId, expectationFragment } = actions
        if (!expectationFragment && !expectationId) {
          return null
        }
        return (
          <Flex justify="end">
            <Button
              type="text"
              aria-label="Edit Expectation"
              icon="edit"
              disabled={!expectationFragment}
              onClick={(e) => {
                e.stopPropagation()
                expectationFragment ? onEdit(expectationFragment) : undefined
              }}
            />
            <DeleteExpectationModal expectationIds={expectationId ? [expectationId] : null} assetId={assetId} />
          </Flex>
        )
      },
    })
  }

  return columns
}

const sortFn = (a: string, b: string) => {
  return a.localeCompare(b, undefined, { numeric: true })
}

const noDomainPlaceholder = {
  domainName: assetDomainName,
  sortValue: firstLexicalCharacter,
  truncateInfo: { truncate: false, fullName: assetDomainName },
}

export const renderRegularExpectationColumn = (record: DataType): JSX.Element => {
  const content = record.expectation.content
  if (content === null) {
    return <></>
  }
  if (typeof content === "string") {
    return <>{content}</>
  }
  return content
}

export const renderGroupExpectationColumn = (record: DataType): JSX.Element => {
  const isEmpty =
    record.children?.length === 0 ||
    (record.children?.length === 1 && record.children[0].expectation.id.includes(noExpectationsEmptyRowId))
  const dqiCategory = (record.expectation.dataQualityIssue as DataQualityIssue) ?? "Other"
  return (
    <Flex align="center" gap="middle">
      <DqiCoverageIcon dqiCategory={dqiCategory} covered={!isEmpty} size={theme.typography.lineHeight.large} />
      <strong>{record.expectation.domainName}</strong>
      <span
        style={{
          color: `${theme.colors.neutralColorPalette.blacks.colorTextTertiary}`,
          fontSize: `${theme.typography.fontSize.extraSmall}`,
          margin: 0,
          lineHeight: theme.typography.lineHeight.large,
          display: "flex",
          alignItems: "center",
        }}
      >
        {isEmpty ? 0 : (record.children?.length ?? 0)}{" "}
        {pluralize("Expectation", isEmpty ? 0 : (record.children?.length ?? 0))}
      </span>
    </Flex>
  )
}

const getExpectationRowData = (data: ExpectationsTable_ExpectationFragment[]): DataType[] => {
  if (!data) {
    return []
  }

  return data.map((exp) => {
    const { domainName, sortValue, truncateInfo } = getDomainData(exp)
    const metaNotes = exp?.renderedContent ? getMetaNotes(exp.renderedContent) : null
    const dataQualityIssue = getDataQualityIssue(exp)
    const content = (
      <Flex vertical gap="middle">
        {renderExpectation({
          expectationConfiguration: {
            ...exp,
            description: exp?.description || getDescriptionFromKwargs(exp?.kwargs),
          },
        })}
        {metaNotes ? <MetaNotes metaNotes={metaNotes} /> : null}
      </Flex>
    )

    return {
      expectation: {
        content,
        renderExpectationColumn: renderRegularExpectationColumn,
        id: exp?.geCloudId ?? "",
        autogenerated: exp?.autogenerated ?? false,
        createdVia: exp?.createdVia ?? null,
        sortValue,
        domainName,
        truncateInfo,
        dataQualityIssue,
      },
      lastValidated: {
        success: exp?.validationResults?.[0]?.success ?? null,
        runTime: exp?.validationResults?.[0]?.runTime
          ? formatLocalCalendarDateWithTime(exp?.validationResults?.[0]?.runTime)
          : null,
      },
      actions: { expectationId: exp?.geCloudId ?? null, expectationFragment: exp as EditExpectationFragment },
      isFilterable: true,
    }
  })
}

const getMultiColSortValue = (columns?: Domain["domainColumns"]) => {
  if (!columns) {
    return firstLexicalCharacter
  }
  if (columns.length === 1) {
    return columns[0]?.name ?? firstLexicalCharacter
  }
  const ascSortedColumns = [...columns].sort((a, b) => {
    const aOrder = a?.order ?? Number.MAX_SAFE_INTEGER
    const bOrder = b?.order ?? Number.MAX_SAFE_INTEGER
    if (aOrder < bOrder) {
      return -1
    }
    if (aOrder > bOrder) {
      return 1
    }
    return 0
  })
  return ascSortedColumns[0]?.name ?? firstLexicalCharacter
}

const getDomainDataFromKwargs = (kwargs: string | undefined | null): DomainData => {
  if (!kwargs) {
    return noDomainPlaceholder
  }

  const parsedKwargs = parseJSONStringConfig(kwargs) as {
    column?: string
    column_list?: string[]
    description?: string
  }
  const parsedDomainName = parsedKwargs?.column ?? parsedKwargs?.column_list?.[0]

  if (parsedDomainName) {
    return {
      ...noDomainPlaceholder,
      domainName: parsedDomainName,
      sortValue: parsedDomainName,
    }
  }

  const parsedDescription = parsedKwargs?.description
  if (parsedDescription) {
    return {
      ...noDomainPlaceholder,
      sortValue: parsedDescription,
    }
  }

  return noDomainPlaceholder
}

const getDomainData = (expectation: ExpectationsTable_ExpectationFragment): DomainData => {
  const { domain, expectationType, kwargs } = expectation

  if (!domain) {
    return getDomainDataFromKwargs(kwargs)
  }

  const MAXCOLNR = 4
  const { column: columnName = "" } = getDomainKwargs({ __typename: "ExpectationConfiguration", domain })

  switch (domain.domainType) {
    case "COLUMN": {
      return {
        domainName: columnName,
        sortValue: columnName,
        truncateInfo: { truncate: false, fullName: columnName },
      }
    }
    case "COLUMN_PAIR":
    case "MULTICOLUMN": {
      const shouldTruncate = (domain.domainColumns?.length ?? 0) > MAXCOLNR
      const domainColumnsCopy = domain.domainColumns ? [...domain.domainColumns] : []
      const showCols = shouldTruncate ? domainColumnsCopy.splice(0, MAXCOLNR) : domain.domainColumns
      const joinColNames = (cols: Domain["domainColumns"] | undefined) =>
        cols ? cols.map((col) => col?.name).join(", ") : ""
      return {
        domainName: joinColNames(showCols),
        sortValue: getMultiColSortValue(domain.domainColumns),
        truncateInfo: {
          truncate: shouldTruncate,
          restColumns: joinColNames(domainColumnsCopy),
          fullName: joinColNames(domain.domainColumns),
        },
      }
    }
    case "TABLE": {
      const domainData = getDomainDataFromKwargs(kwargs)
      const domainName =
        expectationType === customSQLExpectationType ? customSQLDomainName : (domainData.domainName ?? assetDomainName)
      return {
        ...domainData,
        domainName,
        truncateInfo: { truncate: false, fullName: domainName },
      }
    }
    default: {
      Sentry.captureMessage(
        `ExpectationsTable getDomainData: handling unexpected domainType ${domain.domainType} on domain ${domain}`,
      )
      return getDomainDataFromKwargs(kwargs)
    }
  }
}

const getDomainFilter = (data: ExpectationsTable_ExpectationFragment[]) => {
  if (!data?.length) {
    return []
  }
  const domainCol = data.map((exp) => getDomainData(exp))
  const sortedFilterList = domainCol.sort((a, b) => sortFn(a.sortValue, b.sortValue))
  const uniqSortedFilterList = uniqBy(sortedFilterList, "truncateInfo.fullName")

  return uniqSortedFilterList.map(({ domainName, truncateInfo }) => ({
    text: <DomainFilterItem domainName={domainName} truncateInfo={truncateInfo} />,
    value: domainName,
  }))
}

function DomainFilterItem({ domainName, truncateInfo }: Omit<DomainData, "sortValue">) {
  return truncateInfo.truncate ? (
    <Tooltip placement="topRight" title={`...${truncateInfo.restColumns}`}>
      <span>{`${domainName}...`}</span>
    </Tooltip>
  ) : (
    <span>{domainName}</span>
  )
}

function getDescriptionFromKwargs(kwargs: string | undefined | null): string {
  if (!kwargs) {
    return ""
  }
  return (parseJSONStringConfig(kwargs) as { description?: string })?.description ?? ""
}

export function getMetaNotes(renderedContent: (RenderedExpectationFragment | null)[]) {
  const metanotes = renderedContent[0]?.value?.metaNotes
  if (!metanotes) {
    return null
  }
  try {
    return parseMetaNotes(metanotes)
  } catch {
    console.error("Parsing meta notes from expectations table failed.")
    return null
  }
}

const getDataQualityIssue = (
  expectation: FragmentType<typeof GetExpectationMetaInfo_ExpectationFragmentDocument>,
): string | undefined => {
  const metaInfo = getExpectationMetaInfo({ expectation })
  return metaInfo?.dataQualityIssues?.[0]
}

const getGroupedExpectationData = (data: ExpectationsTable_ExpectationFragment[]): DataType[] => {
  if (!data?.length) {
    return []
  }

  const rowData = getExpectationRowData(data)

  const groupedData: Record<string, DataType[]> = {}
  const dqis = DataQualityIssues
  for (const dataQualityIssue of dqis) {
    groupedData[dataQualityIssue] = []
  }

  rowData.forEach((row) => {
    const groupKey = row.expectation.dataQualityIssue || "Other"
    if (!groupedData[groupKey]) {
      groupedData[groupKey] = []
    }
    groupedData[groupKey].push(row)
  })

  Object.values(groupedData).forEach((group) => {
    group.sort((a, b) => {
      return sortFn(a.expectation.sortValue, b.expectation.sortValue)
    })
  })

  const result = Object.entries(groupedData).map(([groupName, groupItems]) => ({
    expectation: {
      content: null,
      renderExpectationColumn: renderGroupExpectationColumn,
      id: `group-${groupName}`,
      autogenerated: false,
      sortValue: groupName,
      domainName: groupName,
      truncateInfo: { truncate: false, fullName: groupName },
      dataQualityIssue: groupName,
    },
    lastValidated: {
      success: null,
      runTime: null,
    },
    actions: {
      expectationId: null,
      expectationFragment: null as unknown as EditExpectationFragment,
    },
    children: groupItems,
    isGroupRow: true,
    isFilterable: false,
  }))

  return result
}
